import {
  TeliaAccordion,
  TeliaAccordionItem,
  TeliaCol,
  TeliaGrid,
  TeliaIcon,
  TeliaLink,
  TeliaRow,
} from '@teliads/components/react';

import BottomFaq from '../../components/custom/bottom-faq/BottomFaq';
import ContentBanner from '../../components/custom/content-banner/ContentBanner';
import MiniParagraphs from '../../components/custom/MiniParagraphs/MiniParagraphs';
import PromotionCardPurPur from '../../components/custom/promotion-card/PromotionCard';
import UspComponent from '../../components/custom/usp-component/UspComponent';

import KeyPointCards from './components/KeyPointCards';
import MediumWhiteCard from './components/MediumWhiteCard';
import RichtextWithSideSection from './components/RichtextSideSection';
import {
  COMPONENT_TYPE_ENUM,
  CARD_TYPE_ENUM,
  FAQ_TYPE_ENUM,
  TeaserContent,
  AssemblyFlexibleProps,
} from './models';

import AnchorTabs from 'components/custom/anchor-tabs/AnchorTabs';
import { BaseCard, CardVariantEnum } from 'components/custom/base-card';
import Breadcrumbs from 'components/custom/breadcrumbs/Breadcrumbs';
import CardContentWrapper from 'components/custom/card-content-wrapper/CardContentWrapper';
import BottomCardsCardDisplay from 'components/custom/card-display-variants/BottomCardsCardDisplay';
import ContentCards from 'components/custom/card-display-variants/ContentCards';
import IntroSectionCardDisplay from 'components/custom/card-display-variants/IntroSectionCardDisplay';
import UspCardsCardDisplay from 'components/custom/card-display-variants/UspCardsCardDisplay';
import Carousel from 'components/custom/carousel/Carousel';
import { CarouselVariant } from 'components/custom/carousel/types';
import ContentBlocksWrapper from 'components/custom/content-block/ContentBlocksWrapper';
import SEO from 'components/custom/SEO/SEO';
import { SocialMediaSharing } from 'components/custom/social-media-sharing/SocialMediaSharing';
import HeroBanner from 'components/voca/hero-banner/HeroBanner';
import './AssemblyFlexible.scss';
import { SectionItem } from 'pages/CodeOfConduct/models';
import { renderPlainTextFromRichText, renderRichText } from 'utils/rich-text-render';

const renderComponent = (component: any, index: number) => {
  switch (component?.type) {
    case COMPONENT_TYPE_ENUM.TEASER:
      switch (component.variant) {
        case 'Promotion-card':
          return (
            <div key={component.title + index} style={{ padding: '32px 0' }}>
              <TeliaGrid>
                <PromotionCardPurPur
                  title={component.title}
                  preamble={component.preamble}
                  cta={component.cta}
                  type={'quaternary'}
                />
              </TeliaGrid>
            </div>
          );
        default:
          return (
            <TeliaGrid key={component.title + index}>
              <ContentBanner
                title={component.title}
                preamble={component.preamble}
                cta={component.cta}
                isReversedImagePosition={component.variant === 'Primary'}
                image={component.image}
                key={(component.title, index)}
                isDividerActive={false}
                videoUrl={component.videoUrl}
              />
            </TeliaGrid>
          );
      }
    case COMPONENT_TYPE_ENUM.USP_ENTITY_WRAPPER:
      return (
        <UspComponent
          title={component.title}
          preamble={component.preamble ? renderRichText(component.preamble.json) : undefined}
          uspList={component.uspList}
          key={component.title + index}
          isTitleCentered={component.isTitleCentered}
          titleVariant={'display-25'}
          backgroundColor={component.backgroundColor ?? '#FFFFFF'}
          solutionsVersion
          cta={
            component.cta && {
              text: component.cta.text,
              url: component.cta.url,
            }
          }
        />
      );
    case COMPONENT_TYPE_ENUM.TEASER_WRAPPER:
      if (component.cardType === CARD_TYPE_ENUM.LARGE_PURPLE) {
        return (
          <IntroSectionCardDisplay
            key={component.title + index}
            title={component.title}
            preamble={component.preamble}
            cta={component.cta}
            items={component.items}
            backgroundColor={component.backgroundColor}
            titleCentered={component.titleCentered}
            isCarousel={component.isCarousel}
          />
        );
      }
      if (component.cardType === CARD_TYPE_ENUM.SMALL_WHITE) {
        return (
          <UspCardsCardDisplay
            key={component.title + index}
            items={component.items}
            title={component.title}
            preamble={component.preamble}
            cta={component.cta}
            backgroundColor={component.backgroundColor}
            titleCentered={component.titleCentered}
            isCarousel={component.isCarousel}
          />
        );
      }
      if (component.cardType === CARD_TYPE_ENUM.SMALL_PURPLE) {
        return (
          <BottomCardsCardDisplay
            key={component.title + index}
            items={component.items}
            title={component.title}
            preamble={component.preamble}
            cta={component.cta}
            titleCentered={component.titleCentered}
            isCarousel={component.isCarousel}
            backgroundColor={component.backgroundColor}
          />
        );
      }
      if (component.cardType === CARD_TYPE_ENUM.MEDIUM_WHITE_BORDERLESS) {
        return (
          <div key={component.title + index} style={{ backgroundColor: component.backgroundColor }}>
            <ContentCards
              cardList={component.items}
              title={component.title}
              preamble={component.preamble}
              cta={component.cta}
              isCarousel={component.isCarousel}
              titleCentered={component.titleCentered}
            />
          </div>
        );
      }
      if (component.cardType === CARD_TYPE_ENUM.MEDIUM_WHITE) {
        return (
          <div
            key={component.title + index}
            style={{ backgroundColor: component.backgroundColor, padding: '32px 0' }}
          >
            <CardContentWrapper
              title={component.title}
              preamble={component.preamble ? renderRichText(component.preamble.json) : undefined}
              isTitleCentered={component.titleCentered}
            >
              {component.isCarousel && (
                <Carousel variant={CarouselVariant.SMALL_FRAME}>
                  {component.items.map((card: TeaserContent) => (
                    <MediumWhiteCard
                      url={card.url}
                      title={card.title}
                      body={card.body}
                      image={card.image}
                      key={`${card.title}-${card.url}`}
                    />
                  ))}
                </Carousel>
              )}
              {!component.isCarousel && (
                <section className="collection-list collection-list--standard">
                  {component.items.map((card: any) => (
                    <div className="collection-list__item" key={card.title}>
                      <MediumWhiteCard
                        url={card.url}
                        title={card.title}
                        body={card.body}
                        image={card.image}
                        key={`${card.title}-${card.url}`}
                      />
                    </div>
                  ))}
                </section>
              )}
            </CardContentWrapper>
          </div>
        );
      }
      if (component.cardType === CARD_TYPE_ENUM.LARGE_BASE_CARDS) {
        return (
          <div
            key={component.title + index}
            style={{ backgroundColor: component.backgroundColor, padding: '32px 0' }}
          >
            <CardContentWrapper
              title={component.title}
              preamble={component.preamble ? renderRichText(component.preamble.json) : undefined}
              isTitleCentered={component.titleCentered}
              cta={component.cta}
            >
              {component.isCarousel && (
                <Carousel>
                  {component.items.map((item: any, index: number) => (
                    <BaseCard
                      key={`${item.title}-${index}`}
                      cardVariant={CardVariantEnum.LARGE}
                      title={item.title}
                      imgSrc={item.image.url}
                      altText={item.image.altText}
                      focusArea={item.image.focusArea}
                      link={item.url}
                      text={item.body ? item.body : undefined}
                    />
                  ))}
                </Carousel>
              )}
              {!component.isCarousel && (
                <section className="intro-section-card-wrapper">
                  {component.items.map((item: any, index: number) => (
                    <BaseCard
                      key={`${item.title}-${index}`}
                      cardVariant={CardVariantEnum.LARGE}
                      title={item.title}
                      imgSrc={item.image.url}
                      altText={item.image.altText}
                      focusArea={item.image.focusArea}
                      link={item.url}
                      text={item.body ? item.body : undefined}
                    />
                  ))}
                </section>
              )}
            </CardContentWrapper>
          </div>
        );
      }
      if (component.cardType === null) {
        return (
          <div
            key={component.title + index}
            style={{
              backgroundColor: component.backgroundColor,
              paddingTop: `${component.titleCentered ? '64px' : '32px'}`,
            }}
          >
            <CardContentWrapper
              title={component.title}
              preamble={component.preamble ? renderRichText(component.preamble.json) : undefined}
              isTitleCentered={component.titleCentered}
              titleVariant={component.titleCentered ? 'display-25' : 'title-300'}
            >
              {component.items.map((item: any, index: number) => (
                <ContentBanner
                  title={item.title}
                  preamble={item.preamble}
                  cta={item.cta}
                  isReversedImagePosition={
                    item.cardType ? item.cardType === 'Primary' : index % 2 === 0
                  }
                  image={item.image}
                  key={(item.title, index)}
                  isDividerActive={false}
                  videoUrl={item.videoUrl}
                />
              ))}
            </CardContentWrapper>
          </div>
        );
      }
      break;
    case COMPONENT_TYPE_ENUM.FAQ_SECTION:
      if (component.faqType === FAQ_TYPE_ENUM.MINI_PARAGRAPHS) {
        return (
          <div
            key={component.title + index}
            style={{
              backgroundColor: component.backgroundColor,
              paddingTop: '32px',
              paddingBottom: '48px',
            }}
          >
            <TeliaGrid>
              <MiniParagraphs
                title={component.title}
                paragraphs={component.items}
                cta={component.cta}
              />
            </TeliaGrid>
          </div>
        );
      }
      if (component.faqType === FAQ_TYPE_ENUM.ACCORDION) {
        return (
          <div
            key={component.title + index}
            style={{ backgroundColor: component.backgroundColor, padding: '32px 0' }}
          >
            <TeliaGrid>
              <TeliaAccordion
                heading={component.title}
                heading-tag="h3"
                dataTestid="midFaqAccordionTitle"
              >
                {component.items.map((content: SectionItem, id: number) => {
                  return (
                    <TeliaAccordionItem heading={content.title} key={(content.title, id)}>
                      {content.body?.json ? renderRichText(content.body.json) : <></>}
                    </TeliaAccordionItem>
                  );
                })}
              </TeliaAccordion>
              {component.cta && (
                <TeliaLink
                  href={component.cta.url}
                  variant="standalone"
                  style={{ margin: '16px 0' }}
                >
                  {component.cta.text}
                  <TeliaIcon name="arrow-right" slot="right" size="sm" />
                </TeliaLink>
              )}
            </TeliaGrid>
          </div>
        );
      }
      if (component.faqType === FAQ_TYPE_ENUM.COMPACT_ACCORDION) {
        return (
          <div
            key={component.title + index}
            style={{ backgroundColor: component.backgroundColor, padding: '32px 0' }}
          >
            <TeliaGrid>
              <BottomFaq title={component.title} items={component.items} cta={component.cta} />
            </TeliaGrid>
          </div>
        );
      }
      if (component.faqType === FAQ_TYPE_ENUM.PURPLE_CARDS) {
        return (
          <div
            key={component.title + index}
            style={{ backgroundColor: component.backgroundColor, padding: '32px 0' }}
          >
            <KeyPointCards items={component.items} title={component.title} cta={component.cta} />
          </div>
        );
      }
      break;
    case COMPONENT_TYPE_ENUM.RICHTEXT_CONTAINER:
      if (!component.sideSection) {
        return (
          <div key={component.richtextContainerId + index} style={{ padding: '32px 0' }}>
            <TeliaGrid>
              <ContentBlocksWrapper
                contentBlocks={component.body.content}
                assetLinks={component.body.links}
                disableAnchorTabs={component.disableAnchorTabs}
                isFullWidth
              />
            </TeliaGrid>
          </div>
        );
      }
      if (component.sideSection) {
        return (
          <RichtextWithSideSection
            component={component}
            key={component.richtextContainerId + index}
          />
        );
      }
      break;

    case COMPONENT_TYPE_ENUM.IMAGE:
      switch (component.fullWidth) {
        case false:
          return (
            <img
              src={component.url}
              alt={component.altText}
              style={{ width: '100%' }}
              key={component.url + index}
            />
          );
        default:
          return (
            <div key={component.url + index} style={{ padding: '32px 0' }}>
              <TeliaGrid>
                <TeliaRow>
                  <TeliaCol width={12}>
                    <img src={component.url} alt={component.altText} style={{ width: '100%' }} />
                  </TeliaCol>
                </TeliaRow>
              </TeliaGrid>
            </div>
          );
      }
    default:
      return null;
  }
};

const AssemblyFlexiblePage = ({
  heroBanner,
  showBreadcrumbs,
  socialMediaShare,
  contentSection,
  anchorTabs,
  seoMetadata,
}: AssemblyFlexibleProps) => {
  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      const offsetTop =
        section.getBoundingClientRect().top +
        window.scrollY -
        (section.offsetTop >= window.scrollY ? 100 : 160);
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  const anchorTabsAdjusted =
    anchorTabs && anchorTabs.length > 0
      ? anchorTabs?.map((tab) => ({
          id: tab.title,
          title: tab.title,
        }))
      : [];

  return (
    <main className="assembly-flexible-page">
      {showBreadcrumbs && <Breadcrumbs title={heroBanner?.title} />}

      {seoMetadata && (
        <SEO
          title={seoMetadata.title}
          meta={seoMetadata.meta}
          socialMedia={seoMetadata.socialMedia}
        />
      )}
      {heroBanner && (
        <HeroBanner
          title={heroBanner.title}
          preamble={
            heroBanner.preamble ? renderPlainTextFromRichText(heroBanner.preamble.json) : <></>
          }
          video={
            heroBanner.videoUrl || heroBanner.image.url
              ? {
                  url: heroBanner.videoUrl,
                  poster: heroBanner.image.url,
                  altText: heroBanner.image.altText,
                }
              : undefined
          }
          billboardEnabled
        />
      )}

      {anchorTabs && anchorTabs.length > 0 && (
        <AnchorTabs links={anchorTabsAdjusted} onTabChange={(id) => scrollToSection(id)} gridless />
      )}

      {socialMediaShare && (
        <TeliaGrid>
          <div className="assembly-flexible-page__social-media-wrapper">
            <SocialMediaSharing />
          </div>
        </TeliaGrid>
      )}

      {contentSection &&
        contentSection.length > 0 &&
        contentSection.map((component, index) => {
          const matchingAnchor = anchorTabs?.find((tab) => tab.id === index.toString());
          return (
            <div
              key={`top-${component.title}-${index}`}
              id={matchingAnchor ? matchingAnchor.title : undefined}
              style={{ backgroundColor: component.backgroundColor }}
            >
              {renderComponent(component, index)}
            </div>
          );
        })}
    </main>
  );
};

export default AssemblyFlexiblePage;
